import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Notification, HeaderComponent } from '@lp-ui-lib';
import { AssetsService, I18nService, SettingsService, UiService } from '@lp-ui-lib';
import {
  ObjectFilter,
  SearchFilterComponent,
  SidebarComponent,
  SpinnerComponent,
  DateFormatSettings,
} from '@lp-ui-lib';
import { TabBarItemComponent } from '../../../components/tab-bar/tab-bar-item/tab-bar-item';
import { TabBarComponent } from '../../../components/tab-bar/tab-bar';
import { NotificationComponent } from '../../../components/notification/notification';
@Component({
  selector: 'app-notifications-list',
  templateUrl: './notifications-list.html',
  styleUrls: ['./notifications-list.scss'],
  imports: [
    TabBarItemComponent,
    TabBarComponent,
    SpinnerComponent,
    SearchFilterComponent,
    SidebarComponent,
    NotificationComponent,
    HeaderComponent,
  ],
})
export class NotificationsListComponent {
  // ========= Inputs / Outputs (Signals) =========
  /** Optional: preselect the "Edge devices" tab (kept false by default) */
  showEdgeDeviceInitial = input<boolean>(false);

  /** Emit when the filter changes (keeps your public API extensible) */
  filterChanged = output<ObjectFilter>();

  /** Emit when the tab bar changes (0: equipment, 1: edge devices) */
  tabBarChanged = output<number>();
  dateFormat = signal<DateFormatSettings | null>(null);

  // ========= Services via inject() =========
  private assetsService = inject(AssetsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  private settingsSvc = inject(SettingsService);

  // ========= Local UI state (Signals) =========
  filterSig: WritableSignal<ObjectFilter> = signal(new ObjectFilter());
  rawNotifications: WritableSignal<Notification[]> = signal([]);
  loading: WritableSignal<boolean> = signal(false);
  /** You previously got this from the stream; set as needed */
  filtered: WritableSignal<boolean> = signal(false);

  /** Whether we're viewing Edge or Equipment notifications */
  showEdgeDeviceNotifications: WritableSignal<boolean> = signal(false);

  // ========= Device flags (computed) =========
  isMobile: Signal<boolean> = computed(() => this.ui.IsMobile());
  isTablet: Signal<boolean> = computed(() => this.ui.IsTablet());
  isDesktop: Signal<boolean> = computed(() => this.ui.IsDesktop());
  private loaded = false;
  private loadingInFlight = false;

  // ========= Derived notifications =========
  notifications: Signal<Notification[]> = computed(() => this.rawNotifications());

  /** Matches your getter `Notifications` (filtered by current tab) */
  Notifications: Signal<Notification[]> = computed(() =>
    this.showEdgeDeviceNotifications()
      ? this.notifications().filter((n: { isEdgeDevice: any }) => n.isEdgeDevice)
      : this.notifications().filter((n: { isEdgeDevice: any }) => !n.isEdgeDevice)
  );

  NumEquipmentNotifications: Signal<number> = computed(
    () => this.notifications().filter((n: { isEdgeDevice: any }) => !n.isEdgeDevice).length
  );

  NumEdgeDeviceNotifications: Signal<number> = computed(
    () => this.notifications().filter((n: { isEdgeDevice: any }) => n.isEdgeDevice).length
  );

  /** Your `notificationsCount: string = '...'` */
  notificationsCount: Signal<string> = computed(() =>
    this.loading() ? '...' : String(this.notifications().length)
  );

  constructor() {
    // Initialize tab selection from input
    this.settingsSvc.getUserSettings().then((settings: any) => {
      this.dateFormat.set(settings.DateFormat);
    });
    effect(() => {
      const fromInput = this.showEdgeDeviceInitial();
      this.showEdgeDeviceNotifications.set(!!fromInput);
    });

    effect(() => {
      if (this.loaded) return;
      this.loaded = true;
      this.loadNotifications();
    });

    // Auto-switch to "Edge" tab if there are only edge-device notifications
    effect(() => {
      const onlyEdge =
        this.NumEdgeDeviceNotifications() > 0 && this.NumEquipmentNotifications() === 0;

      if (onlyEdge && !this.showEdgeDeviceNotifications()) {
        this.showEdgeDeviceNotifications.set(true);
      }
    });

    // Back button behavior & text (same as before)
    this.ui.onNavBack(() => {
      this.router.navigate(['home'], { replaceUrl: true });
    });
    this.ui.setBackText(this.i18n.langString('home'));

    // Apply route data (tab index, back button) like your ngAfterViewInit()
    const data = this.route.snapshot.data as any;
    this.ui.updateTabIndex(data['tabIndex']);
    this.ui.updateBackBtn(data['backBtn']);
  }

  // ========= Data loading (Promise-based using .then()) =========
  public loadNotifications(): void {
    if (this.loadingInFlight) return;
    this.rawNotifications.set([]);
    this.loadingInFlight = true;
    this.loading.set(true);

    this.assetsService
      .loadNotificationsWithFilter()
      .then((result: any) => {
        const notifications = result.notifications ?? [];

        // Normalization (ensure breadcrumbs are arrays)
        const normalized = notifications.map((n: any) => ({
          ...n,
          breadcrumbs: Array.isArray(n.breadcrumbs) ? n.breadcrumbs : [],
        }));

        this.rawNotifications.set(normalized);
        this.filtered.set(!!result.filtered);

        this.setSelectionPreset(normalized);
      })
      .catch((err: any) => {
        console.error('Failed to get notifications:', err);
        this.rawNotifications.set([]);
        this.filtered.set(false);
      })
      .finally(() => {
        this.loadingInFlight = false;
        this.loading.set(false);
      });
  }

  onSearchFilterChanged() {
    // if (JSON.stringify(filter) === JSON.stringify(this.filterSig())) {
    //   return;
    // }
    // this.filterSig.set(filter);
    this.loadNotifications();
  }

  /** Value: 0 = Equipment tab, 1 = Edge devices tab */
  public onNotificationsTabBarChanged(value: number) {
    this.showEdgeDeviceNotifications.set(value === 1);
    this.tabBarChanged.emit(value);
  }

  private setSelectionPreset(list: Notification[] | null | undefined): void {
    if (!Array.isArray(list) || list.length === 0) {
      this.assetsService.updateSelectionPreset([]);
      return;
    }

    const safe = (n: Notification, i: number) => (n.breadcrumbs && n.breadcrumbs[i]) || null;

    const uniq = (value: any, index: number, array: any[]) =>
      value && index === array.findIndex((x) => x?.objectID === value.objectID);

    const regions = list.map((n) => safe(n, 0)).filter(uniq);
    const areas = list.map((n) => safe(n, 1)).filter(uniq);
    const plants = list.map((n) => safe(n, 2)).filter(uniq);

    const selectionPreset: any[] = [];

    if (regions.length === 1) {
      selectionPreset.push(regions[0]);
      if (areas.length === 1) {
        selectionPreset.push(areas[0]);
        if (plants.length === 1) {
          selectionPreset.push(plants[0]);
        }
      }
    }

    this.assetsService.updateSelectionPreset(selectionPreset);
  }
}
